import { toTitleCase } from 'features/dynamicOntology'
import { formatRaw } from 'features/libra/llmDiff/formatFieldValue'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  type FieldValue,
  type FieldValues,
  type ItemProperty,
  type OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  type ArrayTableSection,
  ArrayTableSectionSchema,
} from 'features/libra/sections/arrayTableSection/arrayTableSectionTypes'

export interface NiiArrayTableRow {
  cells: ReadonlyArray<{ label: string; value: string }>
}

/**
 * One array_table section (e.g. Inspection History). Rendered as stacked
 * entry cards rather than a grid table so long summary text wraps reliably
 * in @react-pdf.
 */
export interface NiiArrayTableBlock {
  title: string
  /** Column headers in display order (for optional table layouts). */
  headers: ReadonlyArray<string>
  entries: ReadonlyArray<NiiArrayTableRow>
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** Unwrap AI `{ value, citation }` cell envelopes when present. */
function unwrapCellValue(value: unknown): unknown {
  if (!isPlainObject(value)) return value
  if (!('value' in value)) return value
  if (
    'citation' in value ||
    'source' in value ||
    Object.keys(value).length <= 3
  ) {
    return value.value
  }
  return value
}

function formatCellValue(value: unknown, property?: ItemProperty): string {
  // Match the in-app enum rendering (EnumSelect / ComputedSectionView),
  // which title-cases the stored enum code.
  if (property?.type === 'enum' && typeof value === 'string' && value !== '') {
    return toTitleCase(value)
  }
  return formatRaw(value)
}

function resolveColumns(
  ontologyField: OntologyField | undefined,
  rows: ReadonlyArray<Record<string, unknown>>
): ReadonlyArray<{ key: string; label: string; property?: ItemProperty }> {
  if (
    ontologyField &&
    ontologyField.category === FieldCategory.INPUT &&
    ontologyField.items?.properties
  ) {
    return Object.entries(ontologyField.items.properties)
      .map(([key, property]) => ({
        key,
        label: property.name ?? key,
        property,
      }))
      .sort(
        (a, b) => (a.property?.sortOrder ?? 0) - (b.property?.sortOrder ?? 0)
      )
  }

  const first = rows[0]
  if (!first) return []
  return Object.keys(first)
    .filter((key) => key !== '_id')
    .map((key) => ({ key, label: key }))
}

function normalizeRows(raw: unknown): ReadonlyArray<Record<string, unknown>> {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((row) => {
    const unwrapped =
      isPlainObject(row) && 'value' in row && isPlainObject(row.value)
        ? row.value
        : row
    if (!isPlainObject(unwrapped)) return []
    return [unwrapped]
  })
}

/**
 * Read an array field from ticket fieldValues. Handles:
 * - normal `{ value: [...] }` wrappers
 * - raw arrays (legacy)
 * - llmValue envelope when no accepted value exists yet
 *
 * An explicitly stored array always wins, even when empty: an empty `value`
 * means the reviewer removed (or never accepted) the rows, and the report of
 * record must not resurrect unaccepted AI content the UI does not show.
 */
export function readArrayFieldRows(
  fieldValues: FieldValues,
  fieldId: string
): unknown {
  const fv = fieldValues[fieldId] as FieldValue | unknown
  if (fv === undefined || fv === null) return undefined
  if (Array.isArray(fv)) return fv

  if (isPlainObject(fv) && 'value' in fv) {
    const direct = unwrapFieldValue(fv as FieldValue)
    if (direct !== undefined && direct !== null) return direct

    const llm = (fv as FieldValue).llmValue
    if (isPlainObject(llm) && 'value' in llm && Array.isArray(llm.value)) {
      return llm.value
    }
    if (Array.isArray(llm)) return llm

    return direct
  }

  return undefined
}

/**
 * True for ConditionalForm array_table sections. Classified through the same
 * Zod schema the UI's SectionSchema union uses, so a section renders as a
 * table in the PDF exactly when ArrayTableSectionView renders it in-app.
 */
export function isArrayTableSection(
  section: unknown
): section is ArrayTableSection {
  return ArrayTableSectionSchema.safeParse(section).success
}

/**
 * Builds PDF blocks for ConditionalForm `array_table` sections
 * (Inspection History, etc.). Always emits a titled block when the section
 * exists so missing data is visible (empty entries) rather than silent.
 */
export function buildNiiArrayTableBlocks(
  sections: ReadonlyArray<unknown>,
  ontologyFields: Record<string, OntologyField>,
  fieldValues: FieldValues
): ReadonlyArray<NiiArrayTableBlock> {
  const blocks: NiiArrayTableBlock[] = []

  for (const rawSection of sections) {
    const parsed = ArrayTableSectionSchema.safeParse(rawSection)
    if (!parsed.success) continue
    const section = parsed.data

    const fieldId = section.field
    const ontologyField = ontologyFields[fieldId]
    const rows = normalizeRows(readArrayFieldRows(fieldValues, fieldId))
    const columns = resolveColumns(ontologyField, rows)

    const entries =
      rows.length === 0 || columns.length === 0
        ? []
        : rows.map((row) => ({
            cells: columns.map(({ key, label, property }) => ({
              label,
              value: formatCellValue(unwrapCellValue(row[key]), property),
            })),
          }))

    blocks.push({
      title: section.title ?? ontologyField?.name ?? section.id,
      headers: columns.map((c) => c.label),
      entries,
    })
  }

  return blocks
}
